/* Shared Day Zero add-to-cart.
 *
 * The buy box broadcasts `dz2:tier` whenever the shopper picks a tier. Any
 * button anywhere on the page marked `data-dz2-add` adds THAT tier's variant,
 * so the closer CTA and the "look inside" CTA can never disagree with the
 * buy box about what is being bought or what it costs.
 *
 * Labels marked `data-dz2-price` keep their price suffix in sync too.
 */
(function () {
  if (window.DZ2Cart) return;

  var state = { variant: null, price: null, name: null };

  function syncLabels() {
    if (!state.price) return;
    document.querySelectorAll('[data-dz2-price]').forEach(function (el) {
      var base = el.getAttribute('data-dz2-price') || el.textContent.split('—')[0].trim();
      el.textContent = base + ' — ' + state.price;
    });
  }

  document.addEventListener('dz2:tier', function (e) {
    var d = e.detail || {};
    if (d.variant) state.variant = d.variant;
    if (d.price) state.price = d.price;
    if (d.name) state.name = d.name;
    syncLabels();
  });

  function currentVariant(fallback) {
    return state.variant || fallback || null;
  }

  /* Optional club add-on: when the hero renders a `[data-dz2-addon]` config
   * (product variant + Appstle selling plan), every add-to-cart also adds
   * that subscription. No config on the page = this whole path is inert and
   * every add behaves exactly as before. Failures here must never break the
   * main add, so this always resolves — with the addon's sections response,
   * or null. */
  function addonConfig() {
    var el = document.querySelector('script[data-dz2-addon]');
    if (!el) return null;
    try {
      var c = JSON.parse(el.textContent);
      return (c && c.variant && c.selling_plan) ? c : null;
    } catch (err) { return null; }
  }

  function addonAdd() {
    var c = addonConfig();
    if (!c) return Promise.resolve(null);

    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    // Don't stack the subscription if it's already in the cart — /cart/add
    // would bump its quantity on every extra CTA click.
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var dup = (cart.items || []).some(function (it) {
          return String(it.variant_id) === String(c.variant);
        });
        if (dup) return null;

        var body = new FormData();
        body.append('id', c.variant);
        body.append('quantity', 1);
        body.append('selling_plan', c.selling_plan);
        body.append('sections', 'cart-drawer,cart-icon-bubble');
        body.append('sections_url', window.location.pathname);
        return fetch(url, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
          body: body
        })
          .then(function (r) { return r.json(); })
          .then(function (res) {
            if (res.status) throw new Error(res.description || 'Could not add club add-on');
            return res;
          });
      })
      .catch(function (err) {
        console.error('[dz2] club add-on failed (main item still added):', err);
        return null;
      });
  }

  /* One request for the offer AND the club add-on (/cart/add.js with
   * items: [...]), so a single add reports a single add_to_cart event and the
   * two lines can never land half-way. The add-on line is only included when
   * the page has the config and the cart does not already hold it, so repeat
   * adds never duplicate it, and a customer who removed it in the cart only
   * gets it back with their next add of an offer. If the combined add is
   * rejected, the offer is added alone: the add-on can never cost the sale.
   * Resolves to the response (items + sections) the drawer renders from. */
  function addonConfigured() { return !!addonConfig(); }

  function addonLine() {
    var c = addonConfig();
    if (!c) return Promise.resolve(null);
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var dup = (cart.items || []).some(function (it) {
          return String(it.variant_id) === String(c.variant);
        });
        if (dup) return null;
        return { id: Number(c.variant), quantity: 1, selling_plan: Number(c.selling_plan) };
      })
      .catch(function () { return null; });
  }

  function postItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: items,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json().then(function (res) { return { ok: r.ok, res: res }; }); })
      .then(function (o) {
        if (!o.ok || o.res.status) throw new Error(o.res.description || o.res.message || 'Could not add to cart');
        return o.res;
      });
  }

  function addOffer(line) {
    return addonLine().then(function (extra) {
      if (!extra) return postItems([line]);
      return postItems([line, extra]).catch(function (err) {
        console.error('[dz2] offer + club add-on rejected, adding the offer alone:', err);
        return postItems([line]);
      });
    });
  }

  function add(variantId, btn) {
    if (!variantId) return Promise.reject(new Error('no variant'));

    if (addonConfigured()) {
      var lbl = btn ? btn.innerHTML : null;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      return addOffer({ id: Number(variantId), quantity: 1 })
        .then(function (res) {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(res);
            if (!drawer.classList.contains('active') && typeof drawer.open === 'function') drawer.open();
          } else {
            window.location.href = '/cart';   // no drawer on this template
          }
        })
        .catch(function (err) {
          console.error('[dz2] add to cart failed:', err);
          window.location.href = '/cart';
        })
        .finally(function () {
          if (btn) { btn.disabled = false; if (lbl !== null) btn.innerHTML = lbl; }
        });
    }

    var label = btn ? btn.innerHTML : null;
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    body.append('sections', 'cart-drawer,cart-icon-bubble');
    body.append('sections_url', window.location.pathname);

    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return fetch(url, {
      method: 'POST',
      // Without Accept, Shopify returns the rendered HTML page instead of
      // JSON and this silently falls through to a full navigation.
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) throw new Error(res.description || 'Could not add to cart');
        // Add the club add-on (if configured) before rendering, so the drawer
        // opens showing the complete cart in one paint.
        return addonAdd().then(function (addonRes) {
          var finalRes = addonRes || res;
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(finalRes);
            if (!drawer.classList.contains('active') && typeof drawer.open === 'function') drawer.open();
          } else {
            window.location.href = '/cart';   // no drawer on this template
          }
        });
      })
      .catch(function (err) {
        console.error('[dz2] add to cart failed:', err);
        window.location.href = '/cart';
      })
      .finally(function () {
        if (btn) { btn.disabled = false; if (label !== null) btn.innerHTML = label; }
      });
  }

  // one delegated listener, so buttons rendered later still work
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-dz2-add]');
    if (!btn) return;
    e.preventDefault();
    add(currentVariant(btn.getAttribute('data-dz2-add')), btn);
  });

  window.DZ2Cart = { add: add, state: state, sync: syncLabels, addonAdd: addonAdd, addonConfigured: addonConfigured, addOffer: addOffer };
})();
